import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import EntityLogs from "../../../../../../../../../../components/Common/EntityLogs/EntityLogs";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { LoanApi } from "../../../../../../../../../../services/Api/types";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import { downloadFile } from "../../../../../../../../../../utils";
import LoanPreview from "../../../../../../../../components/Loan/LoanPreview/LoanPreview";
import SendLoanPdfViaEmailModal from "../../../../../../../../components/Loan/SendLoanPdfViaEmailModal";
import SendMailWithLoanToClient from "../../../../../../../../components/Loan/SendMailWithLoanToClient";
import { FormatLoanStatuses } from "../../../../components/FormatLoanStatus";

interface MatchParams {
	loanId: string;
}

const PartnerPreviewLoansPreviewPage = () => {
	const [loan, setLoan] = useState<undefined | LoanApi>(undefined);
	const [showLogs, setShowLogs] = useState(false);
	const [showSendViaEmailModal, setShowSendViaEmailModal] = useState(false);
	const [showSendLoanEmailModal, setShowSendLoanEmailModal] = useState(false);
	const { t } = useTranslation("admin");
	const partner = useSelector(selectPartner);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const params = { include: "organization,partner,items,attachments" };
	const [showCancelLoanModal, setShowCancelLoanModal] = useState<boolean>(false);
	const { loanId } = useParams<MatchParams>();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchLoan();
	}, []);

	const fetchLoan = async () => {
		if (!isNaN(Number(loanId))) {
			try {
				const res = await partnerApi.getLoan(Number(partner?.id), Number(loanId), params);
				setLoan(res);
				handleChangeTabTitle(
					`#${res.reference_id} | ${t("modules.loan.header.title")} | ${t(
						"modules.partner.field.partner_panel.title"
					)}`
				);
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
	};

	if (!loan) return <LoadingContainer />;

	const downloadPdf = async () => {
		await downloadFile(
			() => partnerApi.getLoanPdf(Number(partner?.id), loan.id),
			`${t("modules.loan.field.loan.title")}_${loan.id}.pdf`,
			addFlash
		);
	};

	const onSignLoan = async () => {
		try {
			await confirmation({
				title: "Czy na pewno umowa jest poprawnie podpisana?",
				message:
					"Zatwierdzenie tej akcji spowoduje zaznaczenie umowy jako poprawnie podpisanej i przekazanie jej do akceptacji przez POSNET oraz GoPOS",
			});
			setLoading(true);
			const resp = await partnerApi.acceptLoan(Number(partner?.id), loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};
	const cancelAccept = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.cancel", { ns: "lib" }),
			});
			setLoading(true);
			const resp = await partnerApi.cancelAcceptLoan(Number(partner?.id), loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const onOpen = async () => {
		setLoading(true);
		try {
			const resp = await partnerApi.openLoan(Number(partner?.id), loan.id, params);
			setLoan(resp);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		setLoading(false);
	};

	const visibilityOfCloseLoanButton =
		loan.status === "CLOSED" || loan.status === "OPEN" || loan.status === "DRAFT" || loan.status === "ACCEPTED";

	const renderTitle = () => (
		<>
			{t("modules.loan.header.title")} #{loan.reference_id} <FormatLoanStatuses loan={loan} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			path: `/partnership/${partner?.id}/loans/${loan.id}/edit`,
			variant: "light",
			title: t("common.action.edit", { ns: "lib" }),
			hide: loan.status === "CLOSED",
		},
		{
			variant: "primary",
			title: t("common.action.confirm", { ns: "lib" }),
			hide: loan.status !== "DRAFT",
			action: onOpen,
			loading,
		},
		{
			variant: "primary",
			title: "Podpisz",
			hide: !(loan.status === "OPEN" && loan.signing_status === "OPEN"),
			action: onSignLoan,
			loading,
		},
		{
			hide: !visibilityOfCloseLoanButton,
			title: t("modules.loan.action.cancel_loan.title"),
			action: () => setShowCancelLoanModal(true),
			dropdown: true,
		},
		{
			hide: loan.status === "CLOSED",
			title: t("common.action.download_pdf"),
			action: downloadPdf,
			dropdown: true,
		},
		{
			hide: !((loan.status === "OPEN" || loan.status === "ACCEPTED") && loan.signing_status === "ACCEPTED"),
			title: "Anuluj podpisanie",
			action: cancelAccept,
			dropdown: true,
		},
		{
			title: t("common.word.logs", { ns: "lib" }),
			dropdown: true,
			action: () => setShowLogs(!showLogs),
		},
		{
			hide: loan.status !== "OPEN",
			action: () => setShowSendLoanEmailModal(true),
			title: t("common.action.send_mail_to_client"),
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			path: `/partnership/${partner?.id}/loans/${loan.id}/edit`,
			title: t("common.action.edit", { ns: "lib" }),
			hide: loan.status === "CLOSED",
		},
		{
			title: t("common.action.confirm", { ns: "lib" }),
			hide: loan.status !== "DRAFT",
			action: onOpen,
		},
		{
			title: "Podpisz",
			hide: !(loan.status === "OPEN" && loan.signing_status === "OPEN"),
			action: onSignLoan,
		},
		{
			hide: !visibilityOfCloseLoanButton,
			title: t("modules.loan.action.cancel_loan.title"),
			action: () => setShowCancelLoanModal(true),
		},
		{
			hide: loan.status === "CLOSED",
			title: t("common.action.download_pdf"),
			action: downloadPdf,
		},
		{
			hide: !((loan.status === "OPEN" || loan.status === "ACCEPTED") && loan.signing_status === "ACCEPTED"),
			title: "Anuluj podpisanie",
			action: cancelAccept,
		},
		{
			title: t("common.word.logs", { ns: "lib" }),
			action: () => setShowLogs(!showLogs),
		},
		{
			hide: loan.status !== "OPEN",
			action: () => setShowSendLoanEmailModal(true),
			title: t("common.action.send_mail_to_client"),
		},
	];

	return (
		<div className={"content-preview"}>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			{showLogs && (
				<EntityLogs resourceId={loan.id} resourceType={"LOAN"} onHide={() => setShowLogs(!showLogs)} />
			)}
			{showSendViaEmailModal && (
				<SendLoanPdfViaEmailModal
					isShown={showSendViaEmailModal}
					onHide={() => setShowSendViaEmailModal(false)}
					loanId={loan.id}
				/>
			)}
			{showSendLoanEmailModal && (
				<SendMailWithLoanToClient
					isShown={showSendLoanEmailModal}
					onHide={() => setShowSendLoanEmailModal(false)}
					loanId={loan.id}
					organizationEmail={loan.organization.company_email}
				/>
			)}

			<LoanPreview
				handleUpdateLoan={setLoan}
				loan={loan}
				showCancelLoanModal={showCancelLoanModal}
				setShowCancelLoanModal={setShowCancelLoanModal}
				onFetchLoan={fetchLoan}
			/>
		</div>
	);
};
export default PartnerPreviewLoansPreviewPage;
